import InvoiceGateway from "./invoice-gateway";
import Estimate from "./estimate";
import Invoice from "./invoice";
import formatDate from "@/helpers/formatDate";
import uuid from "uuid";
import BillingRuleApi from "@/orders/api/billing-rule-api";
import parseDate from "@/helpers/parseDate";
import Decimal from "decimal.js";
import UnitPrice from "@/orders/domain/models/unit-price";
import TotalPrice from "@/orders/domain/models/total-price";
import InvoiceItem from "@/usecases/orders/invoice-item";
import EstimateItem from "@/usecases/orders/estimate-item";

export default class InvoiceService {
  public constructor(
    private invoices: InvoiceGateway,
    private billingRuleRepository: BillingRuleApi
  ) {}

  public async find(id: number): Promise<Invoice> {
    const invoice = await this.invoices.find(id);
    if (!invoice) {
      throw new Error("invoice is not found.");
    }
    return invoice;
  }

  public async save(invoice: Invoice): Promise<void> {
    await this.invoices.save(invoice);
  }

  /**
   * ステータスを変更する
   */
  public async changeStatus(id: string, status: string): Promise<void> {
    const invoice = await this.find(Number(id));
    invoice.status = status;
    await this.save(invoice);
  }

  public async invoice(estimate: Estimate): Promise<number> {
    const rule = await this.billingRuleRepository.findByProject(
      String(estimate.projectId)
    );

    const convertToInvoiceItem = (estimateItem: EstimateItem): InvoiceItem => {
      let items: InvoiceItem[] = [];
      if (estimateItem.items.length > 0) {
        items = estimateItem.items.map(item => convertToInvoiceItem(item));
      }
      return {
        id: uuid(),
        unitName: estimateItem.unitName,
        expenseCategoryId: Number(estimateItem.expenseCategoryId),
        unitType: estimateItem.unitType,
        unitPrice: estimateItem.unitPrice,
        amount: estimateItem.amount,
        totalPrice: estimateItem.totalPrice,
        memo: estimateItem.memo,
        items: items
      };
    };
    let items: InvoiceItem[] = estimate.items.map(i => convertToInvoiceItem(i));
    if (estimate.children.length > 0) {
      estimate.children.forEach(child => {
        const itemsOfChild: InvoiceItem[] = child.items.map(i =>
          convertToInvoiceItem(i)
        );
        items = items.concat(itemsOfChild);
      });
    }

    const billingDate = new Date();
    const invoice = {
      estimateId: estimate.id,
      projectId: estimate.projectId,
      projectName: estimate.projectName,
      title: estimate.title,
      status: "created",
      total: estimate.total,
      subtotal: estimate.subtotal,
      tax: estimate.tax,
      taxRate: estimate.taxRate,
      billingDate: formatDate(billingDate),
      dueDate: formatDate(rule.getDueDate(billingDate)),
      bankAccount1: "",
      bankAccount2: "",
      bankAccount3: "",
      deposit: 0,
      depositRequestedAt: "",
      totalWithDeposit: estimate.total,
      items: items
    };
    return await this.invoices.save(invoice);
  }

  public async invoiceWithoutEstimate(projectId?: string): Promise<number> {
    let invoice = new Invoice();
    const billingDate = new Date();
    invoice.billingDate = formatDate(billingDate);
    if (projectId) {
      invoice.projectId = Number(projectId);
      const rule = await this.billingRuleRepository.findByProject(projectId);
      invoice.dueDate = formatDate(rule.getDueDate(billingDate));
    }
    return await this.invoices.save(invoice);
  }

  public async calculate(invoice: Invoice): Promise<Invoice> {
    // Recursive function to calculate totalPrice for each item and its child items
    const calculateItem = (item: InvoiceItem): InvoiceItem => {
      if (item.items.length > 0) {
        // If the item has child items, calculate each of them first
        const items = item.items.map(i => calculateItem(i));

        // Calculate the totalPrice based on the child items' totalPrices
        const totalPrice = new UnitPrice(
          items
            .map(i => new Decimal(i.totalPrice))
            .reduce((sum, p) => sum.plus(p))
        );
        let unitPrice = new UnitPrice(0);
        if (item.amount > 0) {
          unitPrice = new UnitPrice(
            new Decimal(totalPrice.toNumber())
              .div(item.amount)
              .floor()
              .toNumber()
          );
        }

        return {
          ...item,
          expenseCategoryId: 0,
          unitPrice: unitPrice.toNumber(),
          totalPrice: totalPrice.toNumber(),
          items
        };
      } else {
        // If the item has no child items, calculate totalPrice from its own unitPrice and amount
        return {
          ...item,
          totalPrice: new UnitPrice(item.unitPrice)
            .items(item.amount)
            .toNumber()
        };
      }
    };

    // Calculate each item in the invoice
    const items = invoice.items.map(i => calculateItem(i));

    // Calculate the totalPrice of the invoice by summing the totalPrices of all items
    const totalPrice = new TotalPrice(
      items.map(i => new Decimal(i.totalPrice)).reduce((sum, p) => sum.plus(p))
    );

    return {
      ...invoice,
      items,
      subtotal: totalPrice.toNumber(),
      tax: totalPrice.tax(invoice.taxRate).toNumber(),
      total: totalPrice.withTax(invoice.taxRate).toNumber()
    };
  }

  public async destroy(id: number): Promise<void> {
    await this.invoices.destroy(id);
  }

  /**
   * 入金予定日を得る
   */
  public async getDueDate(
    billingDate: string,
    projectId: string
  ): Promise<string> {
    const date = parseDate(billingDate);
    const rule = await this.billingRuleRepository.findByProject(projectId);
    return formatDate(rule.getDueDate(date));
  }
}
